import {
  AccidentImageTypes,
  AccidentStatusTypes,
  EngineTroubleStatusTypes,
  VehicleHeightStatusTypes,
  TroubleSupportTypes,
  FuelTypes,
  TroubleDatetimeTypes,
  FallingWheelStatusTypes,
  FieldTypes,
  SpareTireStatusTypes,
  FlatTireCauseTypes,
  LossKeyStatusTypes,
  InsideVehicleStatusTypes,
  HighwayLocationTypes,
  StorageLocationTypes,
  LocationTypes,
  OthersStatusTypes,
  OthersKsStatusTypes,
  SnowDetailTypes,
  RelationshipTypes,
  CargoTypes,
  DisplacementTypes,
  CargoQuantityTypes,
  VehicleTypes,
  YesNoSelection,
  VehicleBodyPart,
  VehiclePart,
} from 'states/QnState'

/**
 * 車の状況
 */
export const VEHICLE_STATE = {
  ACCIDENT: '事故',
  EMPTY_FUEL: '燃料切れ',
  ENGINE_TROUBLE: 'バッテリー・エンジン関連',
  FALLING_WHEEL: '落輪・乗り上げ',
  FLAT_TIRE: 'パンク',
  LOSS_KEY: 'カギのトラブル',
  OTHERS: 'その他',
}

/**
 * 駐車場の種別文言
 * 共有する項目があるため、ローカルで定数化
 */
const GARAGE_TYPE: OptionResource<StorageLocationTypes> = {
  streetParking: '屋根のない平地の駐車場',
  mechanical: '立体駐車場（機械式）',
  selfDriving: '立体駐車場（自走式）',
}

/**
 * ラベルタイトル
 */
export const VEHICLE_STATE_CONFIRM_TITLE = {
  ACCIDENT_PART: '車体の損傷個所・状況',
  RA_TRANSFER_SC_WISHES: '移動費用特約',
  RA_LODGING_SC_WISHES: '宿泊費用特約',
  RA_LCAR_COST_SC_WISHES: '代わりのお車（レンタカー）について',
  RA_LCAR_COST_SC_SUPPL_WISHES: '補足内容',
} as const

/**
 * 車高チェックあり・なし
 */
export const VEHICLE_HEIGHT_MODIFICATION_CONFIRM = {
  YES: 'あり',
  NO: 'なし',
}

/**
 * 移動費用特約の選択肢
 */
export const RA_TRANSFER_SC_WISHES = {
  YES: '公共交通機関・タクシーを利用する',
  NO: '徒歩または迎えがくるので利用しない',
  THINKING: '検討中',
  CONFIRMATION: {
    TITLE: '移動費用特約',
    YES: '公共交通機関・タクシーを利用する',
    NO: '利用予定なし（迎えが来る・徒歩）',
    THINKING: '検討中',
  },
}

/**
 * 宿泊費用特約の選択肢
 */
export const RA_LODGING_SC_WISHES = {
  YES: '宿泊施設を利用する',
  NO: '宿泊施設を利用しない',
  THINKING: '検討中',
  CONFIRMATION: {
    TITLE: '宿泊費用特約',
    YES: '宿泊施設を利用する',
    NO: '宿泊施設を利用しない',
    THINKING: '検討中',
  },
}

/**
 * 代車費用特約の選択肢
 */
export const RA_LCAR_COST_SC_WISHES = {
  DESCRIPTION:
    'このトラブル車両は、レンタカーなど代わりのお車の費用を補償する特約がついています。',
  YES: 'レンタカーの手配を希望・相談する',
  NO: 'レンタカーを利用しない',
  ALREADY_ARRANGED: 'レンタカーは自身または修理先で手配済（予定）',
  CONFIRMATION: {
    TITLE: '代車費用特約',
    YES: 'レンタカーの手配を希望・相談する',
    NO: '代車（レンタカーなど）を利用しない',
    ALREADY_ARRANGED: 'レンタカーは自身または修理先で手配済（予定）',
  },
}

/**
 * 当日を選択可能な制限日時
 */
export const RESERVATION_DATE_LIMIT = '14:00'

/**
 * 選択可能な最大日数
 */
export const RESERVATION_DATE_MAX = 7

/**
 * 予約日付の内部フォーマット
 */
export const RESERVATION_DATE_FORMAT = 'YYYY/MM/DD'

/**
 * 予約日付のAPI送信時フォーマット
 */
export const RESERVATION_DATE_API_FORMAT = 'MM/DD'

/**
 * 予約完了画面で表示する予約時間
 */
export const RESERVED_TIME = {
  TIME_1: '１０時～１２時',
  TIME_2: '１２時～１４時',
  TIME_3: '１４時～１７時',
}

/**
 * トラブル日時の内部フォーマット
 */
export const TROUBLE_DATE_FORMAT = 'YYYY/MM/DD:HH'

/**
 * トラブル日時の表示フォーマット
 */
export const TROUBLE_DATE_DISPLAY_FORMAT = 'YYYY年MM月DD日HH時'

/**
 * バイクのパンク箇所未選択時
 */
export const EMPTY_SELECTED_TWO_WHEELED_VEHICLE_PART = 0

/** 選択肢向け文言定義の型 */
export type OptionResource<T extends string> = { [k in T]: string }

export type VehicleStateFormItem = {
  formName: string
  description?: string
  caution?: string
}
export type VehicleStateSingleItem = VehicleStateFormItem & {
  label: string
}
export type VehicleStateMultiItem = VehicleStateFormItem & {
  label: Array<string>
}
export type VehicleStateSelectItem<T extends string> = VehicleStateFormItem & {
  options: OptionResource<T>
}
export type VehicleStatePartItem<T extends string> = VehicleStateSelectItem<
  T
> & {
  label: string
}

export type VehicleStateForm = {
  /** 現場写真 */
  accidentImage: VehicleStateSelectItem<AccidentImageTypes>
  /** 事故状況 */
  accidentStatus: VehicleStateSelectItem<AccidentStatusTypes>
  /** エンジントラブル関連 */
  engineTroubleStatus: VehicleStateSelectItem<EngineTroubleStatusTypes>
  /** 搬送先のタイプ(選択肢はCLIENT_SETTINGSから取得) */
  destinationType: VehicleStateFormItem
  /** 都道府県を越えるか希望 */
  crossPrefectures: VehicleStateSingleItem
  /** その他の現場写真 */
  othersImage: VehicleStateSelectItem<AccidentImageTypes>
  /** トラブル対応の希望 */
  troubleSupport: VehicleStateSelectItem<TroubleSupportTypes>
  /** 車高改造の有無 */
  vehicleHeightModification: VehicleStateSingleItem
  /** 車高の状況 */
  vehicleHeightStatus: VehicleStateSelectItem<VehicleHeightStatusTypes>
  /** 車高の状況（共済系ドメイン時） */
  vehicleHeightKsStatus: VehicleStateSelectItem<VehicleHeightStatusTypes>
  /** トラブル発生日時 */
  troubleDatetime: VehicleStateSelectItem<TroubleDatetimeTypes>
  /** 写真撮影が困難な場合は選択 */
  noPhoto: VehicleStateSingleItem
  /** 写真の追加 */
  isAddPhoto: VehicleStateSingleItem
  /** 事故状況を選択（複数選択可） */
  accidentPart: VehicleStatePartItem<VehiclePart>
  /** パンクした箇所を選択（複数選択可） */
  flatTirePart: VehicleStatePartItem<VehicleBodyPart>
  /** バイクでの事故トラブル */
  motorbikeAccident: VehicleStateSingleItem
  /** バイクでのパンクトラブル */
  motorbikeFlatTire: VehicleStateSingleItem
  /** タイヤ軸が複数ある車両のトラブル */
  notApplicableParts: VehicleStateSingleItem
  /** 油種 */
  fuelType: VehicleStateSelectItem<FuelTypes>
  /** 落輪したタイヤの箇所 */
  fallingWheelPart: VehicleStatePartItem<VehicleBodyPart>
  /** 落輪したバイクのタイヤの箇所 */
  motorbikeFallingWheel: VehicleStateSingleItem
  /** 乗り上げたタイヤの箇所 */
  rideupWheelPart: VehicleStatePartItem<VehicleBodyPart>
  /** 乗り上げたバイクのタイヤの箇所 */
  motorbikeRideup: VehicleStateSingleItem
  /** 落輪のトラブル状況 */
  fallingWheelStatus: VehicleStateSelectItem<FallingWheelStatusTypes>
  /** 現場の種類 */
  fieldType: VehicleStateSelectItem<FieldTypes>
  /** スぺアタイヤの状況 */
  spareTireStatus: VehicleStateSelectItem<SpareTireStatusTypes>
  /** スぺアタイヤの状況 (共済系ドメイン時) */
  spareTireKsStatus: VehicleStateSelectItem<SpareTireStatusTypes>
  /** パンクの原因 */
  flatTireCause: VehicleStateSelectItem<FlatTireCauseTypes>
  /** パンクの原因 (共済系ドメイン時)*/
  flatTireKsCause: VehicleStateSelectItem<FlatTireCauseTypes>
  /** カギのトラブル状況 */
  lossKeyStatus: VehicleStateSelectItem<LossKeyStatusTypes>
  /** 車内の状況 */
  insideVehicleStatus: VehicleStateSelectItem<InsideVehicleStatusTypes>
  /** 場所の種別 */
  locationType: VehicleStateSelectItem<LocationTypes>
  /** 高速道路の停車場所 */
  highwayLocation: VehicleStateSelectItem<HighwayLocationTypes>
  /** 自宅保管場所の特徴 */
  homeStorageLocation: VehicleStateSelectItem<StorageLocationTypes>
  /** その他保管場所の特徴 */
  otherStorageLocation: VehicleStateSelectItem<StorageLocationTypes>
  /** 一般道の補足 */
  localLoadSupplement: VehicleStateSingleItem
  /** 高速道路の補足 */
  highwaySupplement: VehicleStateSingleItem
  /** 自宅・通常保管場所の補足 */
  homeStorageSupplement: VehicleStateSingleItem
  /** 他の保管場所の補足 */
  otherStorageSupplement: VehicleStateSingleItem
  /** 舗装されていない道路の補足 */
  noPavedLoadSupplement: VehicleStateSingleItem
  /** その他の補足 */
  otherSupplement: VehicleStateSingleItem
  /** その他のトラブル状況 */
  othersStatus: VehicleStateSelectItem<OthersStatusTypes>
  /** その他のトラブル状況 (共済系ドメイン時) */
  othersKsStatus: VehicleStateSelectItem<OthersKsStatusTypes>
  /** 雪にはまった時の詳細 */
  snowDetail: VehicleStateSelectItem<SnowDetailTypes>
  /** その他の補足情報 */
  othersDetail: VehicleStateSingleItem
}

/** 車の情報・契約者情報以外のリソース */
export type OtherForm = {
  /** 同意 */
  confirmationAgree: VehicleStateSelectItem<YesNoSelection>
}

/** 契約情報のリソース型 */
export type ContractInfoForm = {
  /** 名前(カナ) */
  nameKana: VehicleStateFormItem
  /** 電話番号 */
  phoneNumber: VehicleStateMultiItem
  /** 契約者との関係 */
  relationship: VehicleStateSelectItem<RelationshipTypes>
  /** 契約者名（本人以外の場合） */
  contractorName: VehicleStateSingleItem
  /** ナンバープレート情報 */
  plateNumber: VehicleStateMultiItem
  /** 車両種別 */
  vehicleType: VehicleStateSelectItem<VehicleTypes>
  /** バイクの排気量 */
  bikeDisplacement: VehicleStateSelectItem<DisplacementTypes>
  /** 最大積載量 */
  cargo: VehicleStateSelectItem<CargoTypes>
  /** 搬送時の積み荷の量 */
  cargoQuantity: VehicleStateSelectItem<CargoQuantityTypes>
  /** JAFへの加入の有無 */
  jafMembershipConfirmation: VehicleStateSingleItem
  /** JAF会員番号 */
  jafMembershipNumber: VehicleStateSingleItem
  /** JAF会員名 */
  jafMembershipName: VehicleStateSingleItem
}

/**
 *  新ヘルプボタンの設置にて途中入力送信で正しい情報を送信するため
 *  レンダリングされるまで EditingItem の初期値を[""]にする必要がある。
 *  ローカルストレージの値を key として使用しているため、空文字の key を許容する
 */

/** 車の状況のフォーム情報 */
export const VEHICLE_STATE_FORM: VehicleStateForm = {
  accidentImage: {
    formName: '現場の写真',
    description: '迅速なかけつけのため写真撮影にご協力ください。',
    options: {
      peripheral: '周辺を含めた写真',
      damagedPart: '損傷箇所の写真',
      vehicleCloseUp: '車両近接写真',
      landmark: '付近の目印などの写真',
      carVerification: '車検証などの写真',
      supplement: '上記以外の写真',
    },
  },
  accidentStatus: {
    formName: '事故状況',
    options: {
      '': '',
      car: '車と車の事故',
      object: '車以外との事故（ガードレール・動物など）',
      rollover: '横転',
      falling: '落車',
    },
  },
  engineTroubleStatus: {
    formName: 'トラブルの状況',
    options: {
      '': '',
      battery: 'バッテリー上がり',
      engineNotStart: 'エンジンがかからない',
      engineStop: '走行中のエンスト',
      overheat: 'オーバーヒート',
    },
  },
  destinationType: {
    formName: '自走できない場合の運び先候補',
  },
  crossPrefectures: {
    formName: '都道府県を越えるか希望',
    label: '都道府県を越える希望',
  },
  othersImage: {
    formName: '現場の写真',
    options: {
      peripheral: '周辺を含めた写真',
      damagedPart: '損傷箇所の写真',
      vehicleCloseUp: '車両近接写真',
      landmark: '付近の目印などの写真',
      carVerification: '車検証などの写真',
      supplement: '上記以外の写真',
    },
  },
  troubleSupport: {
    formName: '希望する対応',
    options: {
      '': '',
      emergencyMeasure: '現場での応急処置',
      wrecker: 'レッカー搬送',
    },
  },
  vehicleHeightModification: {
    formName: '車高改造の有無',
    label: '車高を低くしている',
  },
  vehicleHeightStatus: {
    formName: '車高',
    options: {
      '': '',
      moreThan9cm: '車高9cm以上',
      under9cm: '車高9cm未満',
    },
  },
  vehicleHeightKsStatus: {
    formName: '車高',
    options: {
      '': '',
      moreThan9cm: '車高（最低地上高）９cm以上',
      under9cm: '車高（最低地上高）９cm未満',
    },
  },
  noPhoto: {
    formName: '写真撮影が困難な場合',
    label: '写真以外で状況説明',
  },
  isAddPhoto: {
    formName: '車検証等の写真',
    label: '写真',
  },
  accidentPart: {
    formName: '事故状況',
    options: {
      leftFront: '左前',
      rightFront: '右前',
      leftRear: '左後',
      rightRear: '右後',
      allFront: 'フロント全面',
      allRear: 'リア全面',
      front: '前',
      rear: '後',
    },
    label: '損傷して回らないタイヤがある',
  },
  flatTirePart: {
    formName: 'パンクした箇所',
    label: '',
    options: {
      leftFront: '左前',
      rightFront: '右前',
      leftRear: '左後',
      rightRear: '右後',
      front: '前',
      rear: '後',
    },
  },
  motorbikeAccident: {
    formName: 'バイクでの事故トラブル',
    label: 'バイクの破損個所を入力する',
  },
  motorbikeFallingWheel: {
    formName: 'バイクでの落輪トラブル',
    label: 'バイクの落輪箇所を入力する',
  },
  motorbikeRideup: {
    formName: 'バイクでの乗り上げトラブル',
    label: 'バイクの乗り上げ箇所を入力する',
  },
  motorbikeFlatTire: {
    formName: 'バイクでのパンクトラブル',
    label: 'バイクのパンク箇所を入力する',
  },
  notApplicableParts: {
    formName: 'タイヤ軸が複数ある車両のトラブル',
    label: '上記画像に当てはまらない',
  },
  fuelType: {
    formName: '燃料の種類',
    options: {
      '': '',
      regular: 'ガソリン（レギュラー）',
      highOctane: 'ガソリン（ハイオク）',
      lightOil: '軽油',
      electricity: '電気・LPG・水素(その他特殊燃料)',
    },
  },
  fallingWheelPart: {
    formName: '落輪したタイヤの箇所',
    label: '損傷して回らないタイヤがある',
    options: {
      leftFront: '左前',
      rightFront: '右前',
      leftRear: '左後',
      rightRear: '右後',
      front: '前',
      rear: '後',
    },
  },
  rideupWheelPart: {
    formName: '乗り上げたタイヤの箇所',
    label: '損傷して回らないタイヤがある',
    options: {
      leftFront: '左前',
      rightFront: '右前',
      leftRear: '左後',
      rightRear: '右後',
      front: '前',
      rear: '後',
    },
  },
  fallingWheelStatus: {
    formName: 'トラブルの状況',
    options: {
      '': '',
      falling: '落輪',
      rideup: '乗り上げ',
    },
  },
  fieldType: {
    formName: '現場の種類',
    options: {
      '': '',
      gutter: '側溝',
      curb: '縁石・車留め',
      others: 'その他（例：花壇・看板等）\n（100文字まで入力可能）',
    },
  },
  troubleDatetime: {
    formName: 'トラブルが発生した日時・気づいた日時',
    options: {
      '': '',
      todayNow: '先ほど・今しがた（今日）',
      justNow: '先ほど・今しがた',
      todayAM: '今日の午前',
      yesterdayPM: '昨日の午後',
      yesterdayAM: '昨日の午前',
      earlier: '上記以前',
    },
  },
  spareTireStatus: {
    formName: 'スペアタイヤの有無',
    options: {
      '': '',
      yes: 'スペアタイヤあり',
      none: 'なし',
      unknown: '不明',
    },
  },
  spareTireKsStatus: {
    formName: 'スペアタイヤの有無',
    options: {
      '': '',
      yes: 'スペアタイヤあり',
      none: 'なし',
      unknown: 'わからない',
    },
  },
  flatTireCause: {
    formName: 'パンクの原因',
    options: {
      '': '',
      accident: '接触あり（何かを踏んだ等）',
      deterioration: 'タイヤの劣化',
      unknown: '不明',
    },
  },
  flatTireKsCause: {
    formName: 'パンクの原因',
    options: {
      '': '',
      accident: '接触あり（何かを踏んだ等）',
      deterioration: 'タイヤの劣化',
      unknown: 'わからない',
    },
  },
  lossKeyStatus: {
    formName: 'トラブルの状況',
    options: {
      '': '',
      insideCar: '車内にカギ閉じこみ',
      loss: 'カギの紛失',
    },
  },
  insideVehicleStatus: {
    formName: '車内の状況',
    options: {
      confined: '車内にお子さまやペットが閉じ込められている',
      engineIsOn: 'エンジンがついたまま',
    },
  },
  locationType: {
    formName: '場所の種別',
    options: {
      '': '',
      localLoad: '一般道',
      highway: '高速道路',
      home: '自宅・通常保管場所',
      garageParking: '上記以外の車庫・駐車場',
      noPavedLoad: '舗装されていない道路',
      other: 'その他',
    },
  },
  highwayLocation: {
    formName: '停車場所を選択',
    options: {
      '': '',
      mainLine: '本線',
      sapa: 'SA・PA',
    },
  },
  homeStorageLocation: {
    formName: '停車場所の特徴',
    options: GARAGE_TYPE,
  },
  otherStorageLocation: {
    formName: '停車場所の特徴',
    options: GARAGE_TYPE,
  },
  localLoadSupplement: {
    formName: '補足内容',
    label: '目印・店舗名・道幅など',
  },
  highwaySupplement: {
    formName: '補足内容',
    label: 'SA/PA名・方面など',
  },
  homeStorageSupplement: {
    formName: '補足内容',
    label: '施設名・階数・高さ制限など',
  },
  otherStorageSupplement: {
    formName: '補足内容',
    label: '施設名・階数・高さ制限など',
  },
  noPavedLoadSupplement: {
    formName: '補足内容',
    label: '目印・道幅・路面状況',
  },
  otherSupplement: {
    formName: '補足内容',
    label: '田んぼ・砂浜など',
  },
  othersStatus: {
    formName: 'トラブルの状況',
    options: {
      '': '',
      snow: '雪・泥・砂にはまった',
      oil: 'オイル漏れ',
      overheat: 'オーバーヒート',
      others: '上記以外のトラブル',
    },
  },
  othersKsStatus: {
    formName: 'トラブルの状況',
    options: {
      '': '',
      snow: '雪にはまった',
      sand: '泥・砂にはまった',
      oil: 'オイル漏れ',
      overheat: 'オーバーヒート',
      others: '上記以外のトラブル',
    },
  },
  snowDetail: {
    formName: '詳細内容',
    options: {
      '': '',
      snowWithNotNormal:
        '雪にはまった（スタッドレスタイヤやチェーンの装着あり）',
      snowWithNormal: '雪にはまった（ノーマルタイヤ）',
      mudAndSand: '泥・砂にはまった',
    },
  },
  othersDetail: {
    formName: '詳細内容',
    label: '補足情報があれば入力してください',
  },
}

/**
 * 車の状況 上記以前の日時フォーマット
 */
export const VEHICLE_STATE_FORM_EARLIER_FORMAT = 'YYYY/MM/DD:HH'

/**
 * 車の状況 上記以前で入力可能な最小値
 */
export const VEHICLE_STATE_FORM_EARLIER_MINTIME = '2000/01/01:00'

export const CONTRACT_INFO_FORM: ContractInfoForm = {
  nameKana: {
    formName: 'お名前（カナ）',
  },
  phoneNumber: {
    formName: '連絡のとれる電話番号',
    label: ['090', '1234', '5678'],
    description: '姓と名の間にスペースを入れてください。',
  },
  relationship: {
    formName: 'ご契約者様との関係',
    options: {
      '': '',
      self: '本人',
      family: '家族',
      friend: '知人',
      employee: '従業員',
      other: 'その他',
    },
  },
  contractorName: {
    formName: 'ご契約者様名',
    label: '※ご契約者様名に相違がある場合は修正をお願いします',
    caution: 'ご契約者様との関係が本人ではない場合',
  },
  plateNumber: {
    formName: 'トラブル車両のナンバープレート情報',
    description: '(カナ・かな・英数字)',
    label: [
      '入力例：シナガワ300ア1234',
      '入力例：シナガワ',
      '300',
      'ア',
      '1234',
    ],
  },
  bikeDisplacement: {
    formName: '排気量',
    options: {
      '': '',
      under50: '５０ＣＣ以下',
      under125: '５１～１２５ＣＣ',
      under250: '１２６～２５０ＣＣ',
      under400: '２５１～４００ＣＣ',
      over401: '４０１ＣＣ以上',
    },
  },
  vehicleType: {
    formName: '車両種別',
    options: {
      '': '',
      autoMobile: '軽／普通乗用車',
      motorbike: 'バイク',
      truck: 'トラック',
      bus: 'バス',
      other: 'その他',
    },
  },
  cargo: {
    formName: '最大積載量',
    options: {
      '': '',
      lightTruck: '軽トラック',
      under2t: '最大積載量２ｔ未満',
      under4t: '最大積載量２ｔ以上４ｔ以下',
      over4t: '最大積載量４ｔ超',
      over10t: '最大積載量１０ｔ超',
    },
  },
  cargoQuantity: {
    formName: '搬送時の積み荷の量',
    options: {
      '': '',
      full: '満載',
      over50per: '5割以上',
      under50per: '5割未満',
      none: 'なし',
    },
  },
  jafMembershipConfirmation: {
    formName: 'JAFへのご加入（非会員様は選択不要）',
    label: 'JAFに加入しており、JAF手配を希望',
  },
  jafMembershipNumber: {
    formName: '会員番号',
    label: '12桁の会員番号を入力してください。',
  },
  jafMembershipName: {
    formName: '会員の方のお名前',
    label: '別の方が会員の場合はご入力ください。',
  },
}

export const OTHER_FORM: OtherForm = {
  confirmationAgree: {
    formName: '全てのご入力内容、ご注意事項に同意しますか？',
    options: {
      yes: '同意する',
      no: '同意しない',
    },
  },
}
